// LAP-550 V1.2 — Page détail générique pour toutes les cards Marché
// Une seule route /investments/market/:slug[/:sub] alimentée par marketConfig.js
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Stack, Button, Tooltip, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import StarIcon from '@mui/icons-material/Star'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import { makeGetRequest, makePostRequest, makeDeleteRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import AssetTable from '../../_shared/AssetTable'
import { buildOrdering, buildFilters } from '../../_shared/serverQuery'
import MARKET_CONFIG from './marketConfig'
import AddToPortfolioDialog, {
  assetName,
  assetPrice,
  describeError,
} from './AddToPortfolioDialog'

const SLUGS = Object.keys(MARKET_CONFIG)

export default function MarketDetail() {
  const { t } = useTranslation('layout')
  const { slug, sub } = useParams()
  const navigate = useNavigate()
  const [reports, setReports] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [addOpen, setAddOpen] = useState(false)
  // Map asset_ref -> favorite id (favoris persistés pour la sous-vue courante)
  const [favs, setFavs] = useState(new Map())
  const [favBusy, setFavBusy] = useState(false)
  const [valuesHidden, setValuesHidden] = useState(false)

  const config = MARKET_CONFIG[slug] || MARKET_CONFIG.financial
  const hasSubviews = Array.isArray(config.subviews) && config.subviews.length > 0
  const currentSub = hasSubviews
    ? config.subviews.find((s) => s.value === sub) || config.subviews[0]
    : null

  const subType = hasSubviews ? currentSub?.dataType || '' : ''
  // Marchés volumineux (ex. financier, des milliers de titres) : pagination
  // côté serveur — on ne charge qu'une page à la fois pour ne jamais saturer
  // le navigateur (sinon écran blanc). Les petits marchés gardent l'ancien
  // chemin (market-view complet + pagination client).
  const paginated = !!config.paginated
  const [srvRows, setSrvRows] = useState([])
  const [srvCount, setSrvCount] = useState(0)
  const [srvPage, setSrvPage] = useState(0) // 0-based (l'API attend page+1)
  const [srvPageSize, setSrvPageSize] = useState(25)
  const [srvLoading, setSrvLoading] = useState(false)

  // LAP-551 — Recherche / tri / filtres.
  // Sur un marché paginé côté serveur, trier ou filtrer dans le navigateur ne
  // porterait que sur les 25 lignes affichées : le tri « prix décroissant »
  // rendait la plus chère de la page, pas du marché. On remonte donc les trois
  // à l'API. Sur les petits marchés (tout est chargé) le DataGrid s'en charge.
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortModel, setSortModel] = useState([])
  const [filterModel, setFilterModel] = useState({ items: [], linkOperator: 'and' })

  // Anti-rafale : on n'appelle l'API qu'une fois la frappe terminée.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(timer)
  }, [search])

  const ordering = paginated ? buildOrdering(sortModel) : ''
  const filtersParam = paginated ? buildFilters(filterModel) : ''

  // Changer de marché ou de sous-vue remet la liste à zéro : les colonnes ne
  // sont pas les mêmes, un tri sur `variation_1d` n'a aucun sens sur les ETF.
  useEffect(() => {
    setSrvPage(0)
    setSearch('')
    setDebouncedSearch('')
    setSortModel([])
    setFilterModel({ items: [], linkOperator: 'and' })
  }, [config.sourceType, subType])

  // Toute nouvelle recherche / tri / filtre repart de la première page :
  // rester en page 7 d'un résultat qui n'en compte plus que 2 affichait un
  // tableau vide.
  useEffect(() => {
    setSrvPage(0)
  }, [debouncedSearch, ordering, filtersParam])

  // Chemin paginé serveur (marchés volumineux).
  useEffect(() => {
    if (!paginated) return undefined
    let cancelled = false
    const run = async () => {
      setSrvLoading(true)
      const params = new URLSearchParams({
        market: config.sourceType,
        sub: subType,
        page: String(srvPage + 1),
        page_size: String(srvPageSize),
      })
      if (debouncedSearch) params.set('q', debouncedSearch)
      if (ordering) params.set('ordering', ordering)
      if (filtersParam) params.set('filters', filtersParam)
      const { data, status } = await makeGetRequest(
        `/api/investment/market-detail/?${params.toString()}`,
        {},
        true,
      )
      if (cancelled) return
      if (status === 200 && data) {
        setSrvRows(Array.isArray(data.results) ? data.results : [])
        setSrvCount(Number(data.count) || 0)
      } else {
        setSrvRows([])
        setSrvCount(0)
      }
      setSrvLoading(false)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [
    paginated,
    config.sourceType,
    subType,
    srvPage,
    srvPageSize,
    debouncedSearch,
    ordering,
    filtersParam,
  ])

  // Chemin historique (petits marchés) : market-view complet, pagination client.
  useEffect(() => {
    if (paginated) return
    const run = async () => {
      const { data } = await makeGetRequest(
        `/api/investment/market-view/?full=${config.sourceType}&sub=${subType}`,
        {},
        true,
      )
      setReports(data || [])
    }
    run()
  }, [paginated, config.sourceType, subType])

  // La sélection n'a plus de sens quand on change de marché/sous-vue.
  useEffect(() => {
    setSelectionModel([])
  }, [slug, sub])

  // Favoris persistés — chargés et indexés pour le marché/sous-vue courant.
  useEffect(() => {
    const loadFavorites = async () => {
      const { data, status } = await makeGetRequest('/api/investment/favorites/', {}, true)
      if (status !== 200) return
      const list = Array.isArray(data) ? data : data?.results || []
      const map = new Map()
      list
        .filter(
          (f) => f.market_slug === slug && (f.sub_view || '') === (sub || ''),
        )
        .forEach((f) => map.set(String(f.asset_ref), f.id))
      setFavs(map)
    }
    loadFavorites()
  }, [slug, sub])

  // Données + colonnes selon sous-vue
  const { rows, columns, subtitle } = useMemo(() => {
    const section = reports.find((r) => r.type === config.sourceType)
    let raw = []
    let sub_subtitle = ''

    if (hasSubviews) {
      const buckets = Array.isArray(section?.data) ? section.data : []
      const bucket = buckets.find((b) => b.type === currentSub.dataType) || buckets[0]
      raw = bucket?.data || []
      sub_subtitle = bucket?.title || ''
    } else {
      if (config.dataPath && section?.data) {
        raw = section.data[config.dataPath] || []
      } else if (Array.isArray(section?.data)) {
        raw = section.data
      }
    }

    // Les colonnes arrivent déjà typées et formatées de marketConfig
    // (fabriques de _shared/gridCols) : rien à réécrire ici.
    const cols = (currentSub && currentSub.columns) || config.columns || []
    return { rows: raw, columns: cols, subtitle: sub_subtitle }
  }, [reports, config, hasSubviews, currentSub])

  // En mode paginé serveur, les lignes viennent de l'endpoint /market-detail/
  // (une page à la fois) plutôt que du market-view complet.
  const effRows = paginated ? srvRows : rows
  const effSubtitle = paginated ? currentSub?.label || '' : subtitle
  const effCount = paginated ? srvCount : rows.length

  // Lignes avec id stable — partagées entre le tableau et les actions groupées.
  const rowsWithId = useMemo(
    () => effRows.map((r, i) => ({ id: r.id ?? `${slug}-${i}`, ...r })),
    [effRows, slug],
  )
  const selectedRows = useMemo(
    () => rowsWithId.filter((r) => selectionModel.includes(r.id)),
    [rowsWithId, selectionModel],
  )

  // Quand les valeurs sont masquées, on neutralise l'AFFICHAGE des colonnes
  // numériques (prix, volume, cap, delta…) sans toucher aux données réelles.
  // On conserve `valueGetter` : la valeur reste triable et filtrable même
  // masquée (avant, le supprimer cassait le tri de ces colonnes).
  const displayColumns = useMemo(() => {
    if (!valuesHidden) return columns
    const NUMERIC = new Set(['price', 'volume', 'marketcap', 'per', 'score'])
    return columns.map((c) =>
      c.kind === 'delta' || NUMERIC.has(c.field)
        ? { ...c, renderCell: () => '•••', valueFormatter: () => '•••' }
        : c,
    )
  }, [columns, valuesHidden])

  const currentIdx = SLUGS.indexOf(slug)
  const onPrev = () =>
    navigate(`/investments/market/${SLUGS[(currentIdx - 1 + SLUGS.length) % SLUGS.length]}`)
  const onNext = () =>
    navigate(`/investments/market/${SLUGS[(currentIdx + 1) % SLUGS.length]}`)

  const selectedCount = selectedRows.length

  const handleFavorites = async () => {
    if (!selectedCount || favBusy) return
    setFavBusy(true)
    const next = new Map(favs)
    let added = 0
    let removed = 0
    let lastErr = ''
    for (const r of selectedRows) {
      const ref = String(r.id)
      if (next.has(ref)) {
        // eslint-disable-next-line no-await-in-loop
        const res = await makeDeleteRequest(
          `/api/investment/favorites/${next.get(ref)}/`,
          {},
          true,
        )
        if (res && (res.status === 204 || res.status === 200)) {
          next.delete(ref)
          removed += 1
        } else {
          lastErr = describeError(res)
        }
      } else {
        const body = {
          market_slug: slug,
          sub_view: sub || '',
          asset_ref: ref,
          name: assetName(r),
          symbol: r.symbol || '',
          sector: r.sector || r.type || '',
          price: assetPrice(r),
        }
        // eslint-disable-next-line no-await-in-loop
        const res = await makePostRequest('/api/investment/favorites/', body, {}, true)
        if (res && (res.status === 201 || res.status === 200) && res.data) {
          next.set(ref, res.data.id)
          added += 1
        } else {
          lastErr = describeError(res)
        }
      }
    }
    setFavs(next)
    setFavBusy(false)
    if (added || removed) setSelectionModel([])
    if (added)
      toast.success(
        t('investments.detail.favoritesAdded', 'Ajouté(s) aux favoris') + ` (${added})`,
      )
    if (removed)
      toast.info(
        t('investments.detail.favoritesRemoved', 'Retiré(s) des favoris') + ` (${removed})`,
      )
    if (!added && !removed)
      toast.error(
        `${t('investments.detail.favoritesError', 'Échec de la mise à jour des favoris')} — ${lastErr}`,
      )
  }

  // Toutes les lignes sélectionnées sont-elles déjà en favori ?
  const allSelectedFav =
    selectedCount > 0 && selectedRows.every((r) => favs.has(String(r.id)))

  // Clic sur une ligne → fiche détail de la valeur (LAP-548 / LAP-550). On
  // n'ouvre la fiche que pour les lignes ayant un identifiant réel (les ids
  // synthétiques `${slug}-${i}` n'existent pas côté API).
  const handleRowClick = (params) => {
    const rawId = params?.row?.id
    if (rawId == null || String(rawId).startsWith(`${slug}-`)) return
    const base = `/investments/market/${slug}`
    const path = hasSubviews && currentSub?.value
      ? `${base}/${currentSub.value}/asset/${rawId}`
      : `${base}/asset/${rawId}`
    navigate(path)
  }

  return (
    <>
      <DetailPageLayout
        title={t(config.title)}
        subtitle={effSubtitle || t('investments.detail.marketSubtitle', 'Vue détaillée du marché')}
        icon={config.icon}
        accent={config.accent}
        backTo="/investments"
        prevNext={{
          onPrev,
          onNext,
          prevLabel: t('investments.detail.prevCard', 'Card précédente'),
          nextLabel: t('investments.detail.nextCard', 'Card suivante'),
        }}
        chartSubtitleOptions={
          hasSubviews
            ? config.subviews.map((s) => ({ value: s.value, label: s.label }))
            : null
        }
        chartSubtitle={currentSub?.value}
        onChartSubtitleChange={(v) => navigate(`/investments/market/${slug}/${v}`)}
        sumLabel={t('investments.detail.assetsCount', 'Actifs listés')}
        sumValue={effCount}
        chart={null}
        tableTitle={t('investments.detail.assetsList', 'Liste des actifs')}
        tableCount={effCount}
        tableContent={
          <AssetTable
            rows={rowsWithId}
            columns={displayColumns}
            emptyLabel={t('investments.portfolio.noRows')}
            onRowClick={handleRowClick}
            checkboxSelection
            selectionModel={selectionModel}
            onSelectionModelChange={(m) => setSelectionModel([...m])}
            loading={paginated ? srvLoading : false}
            pageSize={paginated ? srvPageSize : 25}
            searchable
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={t(
              'investments.detail.searchAssets',
              'Rechercher un nom, un symbole, un secteur…',
            )}
            sortModel={paginated ? sortModel : undefined}
            onSortModelChange={paginated ? (m) => setSortModel(m) : undefined}
            filterModel={paginated ? filterModel : undefined}
            onFilterModelChange={paginated ? (m) => setFilterModel(m) : undefined}
            {...(paginated
              ? {
                  paginationMode: 'server',
                  rowCount: srvCount,
                  page: srvPage,
                  onPageChange: (p) => setSrvPage(p),
                  onPageSizeChange: (s) => {
                    setSrvPageSize(s)
                    setSrvPage(0)
                  },
                }
              : {})}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                selectedCount
                  ? t('investments.detail.addToPortfolio', 'Ajouter aux investissements')
                  : t('investments.detail.selectAssetsFirst', 'Sélectionnez au moins un actif')
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!selectedCount}
                  onClick={() => setAddOpen(true)}
                  startIcon={<ShoppingCartOutlinedIcon />}
                  sx={{
                    backgroundColor: config.accent,
                    color: '#001b42',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: config.accent, filter: 'brightness(1.08)' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' },
                  }}
                >
                  {t('investments.detail.addSelectedToPortfolio', 'Ajouter au portefeuille')}
                  {selectedCount ? ` (${selectedCount})` : ''}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !selectedCount
                  ? t('investments.detail.selectAssetsFirst', 'Sélectionnez au moins un actif')
                  : allSelectedFav
                    ? t('investments.detail.removeFromFavorites', 'Retirer des favoris')
                    : t('investments.detail.addToFavorites', 'Ajouter aux favoris')
              }
            >
              <span>
                <IconButton
                  disabled={!selectedCount || favBusy}
                  onClick={handleFavorites}
                  sx={{ color: allSelectedFav ? '#ffce54' : 'rgba(255,255,255,0.85)' }}
                >
                  {allSelectedFav ? (
                    <StarIcon fontSize="small" />
                  ) : (
                    <StarOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                valuesHidden
                  ? t('investments.detail.showValues', 'Afficher les valeurs')
                  : t('investments.detail.hideValues', 'Masquer les valeurs')
              }
            >
              <IconButton
                onClick={() => setValuesHidden((v) => !v)}
                sx={{ color: valuesHidden ? '#63a0ff' : 'rgba(255,255,255,0.6)' }}
              >
                {valuesHidden ? (
                  <VisibilityOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      <AddToPortfolioDialog
        open={addOpen}
        assets={selectedRows}
        onClose={() => setAddOpen(false)}
        onAdded={() => setSelectionModel([])}
      />
    </>
  )
}
